/**
 * The support page.
 *
 * Shaped around who is asking rather than around our org chart, because the
 * two audiences here want opposite things and one of them should not be
 * writing to us at all: a signer with a question about what an agreement
 * *says* needs the company that sent it, not the company that delivered it.
 * Routing them correctly is the most useful thing this page does.
 *
 * There is no contact form. A form would mean another endpoint accepting
 * untrusted input, somewhere to store it, spam handling, and a new category of
 * personal data to account for in the Privacy Policy — in exchange for saving
 * the sender one click. An address they can copy is better until the volume
 * justifies the machinery.
 */
import { COMPANY } from './legal';

export type SupportChannel = {
  audience: string;
  when: string;
  action: 'email' | 'sender' | 'self';
  detail: string;
  cta?: { label: string; to: string };
};

export const SUPPORT = {
  title: 'Support',
  description:
    'How to get help with eSignLoop: who to contact, what to include, and the '
    + 'questions that are faster to answer yourself.',
  email: COMPANY.contact,
  security: COMPANY.contact,

  channels: [
    {
      audience: 'You were sent an agreement to sign',
      when: 'A question about what the agreement says, or who sent it',
      action: 'sender',
      detail:
        'Contact the organization that sent it to you — their name is on the '
        + 'signing page and in the email you received. We deliver agreements '
        + 'and record evidence about the signing; we are not a party to the '
        + 'agreement and cannot change its terms, extend a deadline or tell '
        + 'you what it means. The same goes for correcting or deleting your '
        + 'details: the sender decides that, and we act on their instruction.',
    },
    {
      audience: 'You were sent an agreement to sign',
      when: 'The link does not work, or the code never arrived',
      action: 'email',
      detail:
        'Write to us. Check the junk folder first — one-time codes are the '
        + 'messages most likely to land there. Include the name of the '
        + 'organization that sent the agreement and the address the link was '
        + 'sent to. Do not send us the link itself: it is a credential.',
    },
    {
      audience: 'You send agreements',
      when: 'Something is wrong, or you are stuck',
      action: 'email',
      detail:
        'Write to us from the email address on the account. Include what you '
        + 'were doing, what you expected and what happened instead. If it '
        + 'concerns a specific agreement, the agreement title and the time it '
        + 'happened are usually enough for us to find it.',
    },
    {
      audience: 'You send agreements',
      when: 'Billing, credits or cancellation',
      action: 'self',
      detail:
        'Most of this is self-service and faster than asking us: buy credits, '
        + 'switch automatic top-up on or off, change the card, or turn off '
        + 'renewal — all from Billing in your dashboard, without contacting '
        + 'anybody. Write to us if a charge looks wrong.',
      cta: { label: 'Open Billing', to: '/app/billing' },
    },
    {
      audience: 'You are checking a document',
      when: 'You need to verify a signed agreement',
      action: 'self',
      detail:
        'No account and no conversation required. Drop the signed PDF into '
        + 'the public verifier and it will tell you whether the bytes match '
        + 'the record. Evidence exports verify offline too, using the '
        + 'published key.',
      cta: { label: 'Open the verifier', to: '/verify' },
    },
    {
      audience: 'You found a security problem',
      when: 'A vulnerability, an exposed credential, anything that worries you',
      action: 'email',
      detail:
        'Please tell us before telling anyone else, and we will not take '
        + 'action against you for reporting in good faith. Put "security" in '
        + 'the subject. Include enough to reproduce it. Do not test against '
        + 'other people’s agreements, do not access data that is not yours, '
        + 'and do not run anything that would degrade the service for others.',
    },
    {
      audience: 'You have a privacy request',
      when: 'Access, correction, export or deletion of personal data',
      action: 'email',
      detail:
        'If you hold an account, write to us and say what you need. If you '
        + 'are a signer, the organization that sent you the agreement is the '
        + 'controller of that data and we will pass your request to them. '
        + 'Deletion of completed agreements is constrained by retention and '
        + 'the append-only evidence record — the Privacy Policy explains why '
        + 'rather than leaving you to find out.',
      cta: { label: 'Read the Privacy Policy', to: '/privacy' },
    },
    {
      audience: 'You want to report misuse',
      when: 'Someone is using the service to do something they should not',
      action: 'email',
      detail:
        'Tell us what you saw and when. Include the sending organization’s '
        + 'name if you have it. We suspend accounts for the conduct listed in '
        + 'the Terms of Service, and suspension stops new sends without '
        + 'deleting anyone’s completed agreements.',
      cta: { label: 'Read the Terms', to: '/terms' },
    },
  ] as SupportChannel[],

  include: [
    'The email address on the account, if you have one.',
    'What you were trying to do, and what happened instead.',
    'The title of the agreement and roughly when, if it concerns one.',
    'Anything you already tried.',
  ],

  never: [
    'A signing link or a completed-copy link. Those are credentials — anyone '
      + 'holding one can open the agreement.',
    'A one-time passcode.',
    'An API key. If you think one has leaked, revoke it in the dashboard '
      + 'first and then tell us.',
    'Card numbers. We never need them, and we are not set up to receive them.',
  ],

  expectations:
    'We are a small team and we do not offer a guaranteed response time — the '
    + 'Terms of Service say so rather than implying otherwise. We read '
    + 'everything that arrives and answer as quickly as we can, and we '
    + 'prioritise anything that has stopped somebody signing or looks like a '
    + 'security problem.',
};
